import { useCurrentEditor } from '@tiptap/react'
import {
  AlignLeft,
  Bold,
  Code as CodeIcon,
  CornerDownLeft,
  Eraser,
  FileCode,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Italic,
  List,
  ListOrdered,
  LucideIcon,
  Minus,
  Palette,
  Quote,
  Redo,
  Strikethrough,
  Trash2,
  Undo,
} from 'lucide-react'

interface MenuButtonConfig {
  icon: LucideIcon
  action: (editor: any) => void
  isActive?: (editor: any) => boolean
  canExecute?: (editor: any) => boolean
  title: string
}

interface MenuGroupConfig {
  buttons: MenuButtonConfig[]
  separator?: boolean
}

const menuConfig: MenuGroupConfig[] = [
  {
    buttons: [
      {
        icon: Bold,
        action: (editor) => editor.chain().focus().toggleBold().run(),
        isActive: (editor) => editor.isActive('bold'),
        canExecute: (editor) => editor.can().chain().focus().toggleBold().run(),
        title: 'Bold',
      },
      {
        icon: Italic,
        action: (editor) => editor.chain().focus().toggleItalic().run(),
        isActive: (editor) => editor.isActive('italic'),
        canExecute: (editor) =>
          editor.can().chain().focus().toggleItalic().run(),
        title: 'Italic',
      },
      {
        icon: Strikethrough,
        action: (editor) => editor.chain().focus().toggleStrike().run(),
        isActive: (editor) => editor.isActive('strike'),
        canExecute: (editor) =>
          editor.can().chain().focus().toggleStrike().run(),
        title: 'Strikethrough',
      },
      {
        icon: CodeIcon,
        action: (editor) => editor.chain().focus().toggleCode().run(),
        isActive: (editor) => editor.isActive('code'),
        canExecute: (editor) => editor.can().chain().focus().toggleCode().run(),
        title: 'Inline Code',
      },
    ],
    separator: true,
  },
  {
    buttons: [
      {
        icon: Eraser,
        action: (editor) => editor.chain().focus().unsetAllMarks().run(),
        title: 'Clear Marks',
      },
      {
        icon: Trash2,
        action: (editor) => editor.chain().focus().clearNodes().run(),
        title: 'Clear Nodes',
      },
    ],
    separator: true,
  },
  {
    buttons: [
      {
        icon: AlignLeft,
        action: (editor) => editor.chain().focus().setParagraph().run(),
        isActive: (editor) => editor.isActive('paragraph'),
        title: 'Paragraph',
      },
      {
        icon: Heading1,
        action: (editor) =>
          editor.chain().focus().toggleHeading({ level: 1 }).run(),
        isActive: (editor) => editor.isActive('heading', { level: 1 }),
        title: 'Heading 1',
      },
      {
        icon: Heading2,
        action: (editor) =>
          editor.chain().focus().toggleHeading({ level: 2 }).run(),
        isActive: (editor) => editor.isActive('heading', { level: 2 }),
        title: 'Heading 2',
      },
      {
        icon: Heading3,
        action: (editor) =>
          editor.chain().focus().toggleHeading({ level: 3 }).run(),
        isActive: (editor) => editor.isActive('heading', { level: 3 }),
        title: 'Heading 3',
      },
      {
        icon: Heading4,
        action: (editor) =>
          editor.chain().focus().toggleHeading({ level: 4 }).run(),
        isActive: (editor) => editor.isActive('heading', { level: 4 }),
        title: 'Heading 4',
      },
      {
        icon: Heading5,
        action: (editor) =>
          editor.chain().focus().toggleHeading({ level: 5 }).run(),
        isActive: (editor) => editor.isActive('heading', { level: 5 }),
        title: 'Heading 5',
      },
      {
        icon: Heading6,
        action: (editor) =>
          editor.chain().focus().toggleHeading({ level: 6 }).run(),
        isActive: (editor) => editor.isActive('heading', { level: 6 }),
        title: 'Heading 6',
      },
    ],
    separator: true,
  },
  {
    buttons: [
      {
        icon: List,
        action: (editor) => editor.chain().focus().toggleBulletList().run(),
        isActive: (editor) => editor.isActive('bulletList'),
        title: 'Bullet List',
      },
      {
        icon: ListOrdered,
        action: (editor) => editor.chain().focus().toggleOrderedList().run(),
        isActive: (editor) => editor.isActive('orderedList'),
        title: 'Ordered List',
      },
    ],
    separator: true,
  },
  {
    buttons: [
      {
        icon: FileCode,
        action: (editor) => editor.chain().focus().toggleCodeBlock().run(),
        isActive: (editor) => editor.isActive('codeBlock'),
        title: 'Code Block',
      },
      {
        icon: Quote,
        action: (editor) => editor.chain().focus().toggleBlockquote().run(),
        isActive: (editor) => editor.isActive('blockquote'),
        title: 'Blockquote',
      },
      {
        icon: Minus,
        action: (editor) => editor.chain().focus().setHorizontalRule().run(),
        title: 'Horizontal Rule',
      },
      {
        icon: CornerDownLeft,
        action: (editor) => editor.chain().focus().setHardBreak().run(),
        title: 'Hard Break',
      },
    ],
    separator: true,
  },
  {
    buttons: [
      {
        icon: Undo,
        action: (editor) => editor.chain().focus().undo().run(),
        canExecute: (editor) => editor.can().chain().focus().undo().run(),
        title: 'Undo',
      },
      {
        icon: Redo,
        action: (editor) => editor.chain().focus().redo().run(),
        canExecute: (editor) => editor.can().chain().focus().redo().run(),
        title: 'Redo',
      },
    ],
    separator: true,
  },
  {
    buttons: [
      {
        icon: Palette,
        action: (editor) => editor.chain().focus().setColor('#958DF1').run(),
        isActive: (editor) =>
          editor.isActive('textStyle', { color: '#958DF1' }),
        title: 'Purple Color',
      },
    ],
  },
]

interface MenuButtonProps {
  config: MenuButtonConfig
  editor: any
}

const MenuButton = ({ config, editor }: MenuButtonProps) => {
  const { icon: Icon, action, isActive, canExecute, title } = config

  const active = isActive?.(editor) ?? false
  const disabled = canExecute ? !canExecute(editor) : false

  const baseClasses = 'p-2 rounded transition-colors duration-150'
  const activeClasses = active
    ? 'bg-indigo-500 text-indigo-100'
    : 'hover:bg-gray-100'
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : ''

  return (
    <button
      onClick={() => !disabled && action(editor)}
      disabled={disabled}
      className={`${baseClasses} ${activeClasses} ${disabledClasses}`}
      title={title}
    >
      <Icon size={16} />
    </button>
  )
}

const Separator = () => <div className='w-px h-6 bg-[#0F16240D] mx-1' />

export const MenuBar = () => {
  const { editor } = useCurrentEditor()

  if (!editor) {
    return null
  }

  return (
    <div className='control-group  bg-[#fcfcfc] rounded-t-lg px-8 py-2'>
      <div className='button-group flex flex-wrap gap-2 items-center'>
        {menuConfig.map((group, groupIndex) => (
          <div key={groupIndex} className='flex items-center gap-2'>
            {group.buttons.map((buttonConfig, buttonIndex) => (
              <MenuButton
                key={buttonIndex}
                config={buttonConfig}
                editor={editor}
              />
            ))}
            {group.separator && groupIndex < menuConfig.length - 1 && (
              <Separator />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
